import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Error Boundary Component untuk menangkap JavaScript errors di frontend
 * Mencegah crash seluruh aplikasi ketika satu komponen error
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      eventId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);
    
    this.setState({ errorInfo });

    // In production, you would send this to Sentry or other error tracking service
    // Example: Sentry.captureException(error, { extra: errorInfo });
    
    // Log to backend for monitoring
    this.logErrorToBackend(error, errorInfo);
  }

  async logErrorToBackend(error, errorInfo) {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/log-client-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: error.toString(),
          stack: error.stack,
          componentStack: errorInfo?.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });
    } catch (e) {
      // Silently fail - don't want error logging to cause more errors
      console.warn('[ErrorBoundary] Failed to log error to backend:', e);
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 dark:bg-zinc-900">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center dark:bg-zinc-900">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 dark:bg-red-950/40">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>

            {/* Error Title */}
            <h1 className="text-xl font-bold text-gray-900 mb-2 dark:text-zinc-50">
              Oops! Terjadi Kesalahan
            </h1>

            {/* Error Description */}
            <p className="text-gray-600 mb-6">
              Maaf, terjadi kesalahan yang tidak terduga. 
              Tim kami sudah diberitahu dan sedang memperbaiki masalah ini.
            </p>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left dark:bg-zinc-800">
                <p className="text-sm font-mono text-red-600 break-all dark:text-red-400">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre className="text-xs text-gray-500 mt-2 overflow-auto max-h-32 dark:text-zinc-400">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={this.handleRetry}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Coba Lagi
              </Button>
              
              <Button 
                onClick={this.handleGoHome}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Home className="w-4 h-4" />
                Ke Dashboard
              </Button>
            </div>

            {/* Support Info */}
            <p className="text-xs text-slate-500 mt-6 dark:text-zinc-400">
              Jika masalah berlanjut, hubungi support@nerava.id
            </p>
          </div>
        </div>
      );
    }

    // Render children normally when there's no error
    return this.props.children;
  }
}

/**
 * Higher-order component untuk wrap komponen dengan ErrorBoundary
 */
export function withErrorBoundary(WrappedComponent, fallbackProps = {}) {
  return function WithErrorBoundary(props) {
    return (
      <ErrorBoundary {...fallbackProps}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
